import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {
  getPlayersTeamSessionGroup,
  getTeamSessionGroupDetail,
} from '../api/teamSessionGroupAPI';
import {TeamSessionGroup} from '../interfaces';

export interface TeamSessionGroupState {
  teamSessionGroup: TeamSessionGroup | null;
  status: 'idle' | 'loading' | 'failed';
  error: Error | null;
}

const initialState: TeamSessionGroupState = {
  teamSessionGroup: null,
  status: 'idle',
  error: null,
};

// The function below is called a thunk and allows us to perform async logic. It
// can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
// will call the thunk with the `dispatch` function as the first argument. Async
// code can then be executed and other actions can be dispatched. Thunks are
// typically used to make async requests.
const getTeamSessionGroupDetailAsync = createAsyncThunk(
  'teamSessionGroup/getTeamSessionGroupDetail',
  async (id: number, {rejectWithValue}) => {
    try {
      const response = await getTeamSessionGroupDetail(id);
      // The value we return becomes the `fulfilled` action payload
      return response;
    } catch (err) {
      return rejectWithValue({
        name: err instanceof Error ? err.name : 'Unknown Error',
        message: err instanceof Error ? err.message : 'Unknown Error',
      });
    }
  },
);

const getPlayersTeamSessionGroupAsync = createAsyncThunk(
  'teamSessionGroup/getPlayersTeamSessionGroupAsync',
  async (
    {id, profileId}: {id: number; profileId: number},
    {rejectWithValue},
  ) => {
    try {
      const response = await getPlayersTeamSessionGroup(id, profileId);
      return response;
    } catch (err) {
      return rejectWithValue({
        name: err instanceof Error ? err.name : 'Unknown Error',
        message: err instanceof Error ? err.message : 'Unknown Error',
      });
    }
  },
);

export const teamSessionGroupSlice = createSlice({
  name: 'teamSessionGroup',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {},
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: builder => {
    builder
      .addCase(getTeamSessionGroupDetailAsync.pending, state => {
        state.status = 'loading';
      })
      .addCase(getTeamSessionGroupDetailAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.teamSessionGroup = action.payload;
        state.error = null;
      })
      .addCase(getTeamSessionGroupDetailAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.teamSessionGroup = null;
        state.error = action.payload as Error;
      })
      .addCase(getPlayersTeamSessionGroupAsync.pending, state => {
        state.status = 'loading';
      })
      .addCase(getPlayersTeamSessionGroupAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.teamSessionGroup = action.payload;
        state.error = null;
      })
      .addCase(getPlayersTeamSessionGroupAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.teamSessionGroup = null;
        state.error = action.payload as Error;
      });
  },
});

export default teamSessionGroupSlice.reducer;

export {getTeamSessionGroupDetailAsync, getPlayersTeamSessionGroupAsync};
